/**
 * Thin re-export over the shared `@pc/ui` hooks so existing
 * `apps/web/src/settings/**` imports keep working. The settings pages
 * historically read role via this module-local file; the shared
 * implementation now lives in `@pc/ui` so module pages (Clients /
 * Products / Orders / Contracts / etc.) can consume the same
 * react-query cache entry without depending on the app shell.
 *
 * If you're authoring a new page outside `apps/web/src/settings/`,
 * import from `@pc/ui` directly rather than threading through this
 * file.
 */
import { useMemo } from 'react';
import {
  useMyOrgRole as useMyOrgRoleShared,
  isAdminRole,
  type OrgRole,
} from '@pc/ui';
import { useActiveContext } from '../auth/ActiveContextProvider';

export function useMyOrgRole() {
  const { activeOrgId } = useActiveContext();
  return useMyOrgRoleShared(activeOrgId ?? null);
}

export { isAdminRole };
export type { OrgRole };

/**
 * Memoize a derived boolean over the role so callers can do
 * `const isAdmin = useIsAdminFromMyOrgRole();` without recomputing
 * `isAdminRole` on every render.
 */
export function useIsAdminFromMyOrgRole(): boolean {
  const { data: role } = useMyOrgRole();
  return useMemo(() => isAdminRole(role), [role]);
}
